import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiVideo, FiPhone, FiMapPin, FiCalendar, FiClock, FiCreditCard, FiCheckCircle, FiArrowLeft, FiArrowRight, FiShield, FiLock, FiAlertCircle, FiStar, FiSmartphone, FiCopy, FiCheck, FiImage } from 'react-icons/fi';

function BookingModern({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lawyerIdParam = searchParams.get('lawyer_id');
  const [currentStep, setCurrentStep] = useState(1);
  const [serviceType, setServiceType] = useState('video');
  const [duration, setDuration] = useState('60');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('11:00 AM');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [transactionId, setTransactionId] = useState('');
  const [paymentSettings, setPaymentSettings] = useState({});
  const [copiedUpi, setCopiedUpi] = useState(false);
  const [lawyerInfo, setLawyerInfo] = useState(null);
  const [allLawyers, setAllLawyers] = useState([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState(lawyerIdParam || '');
  const [lawyerLoading, setLawyerLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/settings`);
        const data = await res.json();
        if (data.success) {
          setPaymentSettings(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch payment settings:', err);
      }
    };
    fetchPaymentSettings();
  }, []);

  // Fetch all lawyers list
  useEffect(() => {
    const fetchLawyers = async () => {
      try {
        setLawyerLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyers`);
        const data = await res.json();
        if (data.success && data.data.length > 0) {
          setAllLawyers(data.data);
          // If lawyer_id is in URL, select that lawyer
          if (lawyerIdParam) {
            const found = data.data.find(l => String(l.id) === String(lawyerIdParam));
            if (found) {
              setLawyerInfo({
                id: found.id,
                name: found.full_name,
                specialty: found.specialty || 'General Practice',
                avatar: found.profile_image ? `${process.env.REACT_APP_API_URL}${found.profile_image}` : null,
                rating: found.successRate ? (found.successRate / 20).toFixed(1) : '4.5',
                reviews: found.cases ? Math.floor(found.cases * 0.7) : 0,
                rate: found.rate || 2500,
              });
              setSelectedLawyerId(String(found.id));
            } else {
              // Lawyer ID not found, use first available
              const first = data.data[0];
              setLawyerInfo({
                id: first.id,
                name: first.full_name,
                specialty: first.specialty || 'General Practice',
                avatar: first.profile_image ? `${process.env.REACT_APP_API_URL}${first.profile_image}` : null,
                rating: first.successRate ? (first.successRate / 20).toFixed(1) : '4.5',
                reviews: first.cases ? Math.floor(first.cases * 0.7) : 0,
                rate: first.rate || 2500,
              });
              setSelectedLawyerId(String(first.id));
            }
          } else {
            // No lawyer_id param, use first lawyer
            const first = data.data[0];
            setLawyerInfo({
              id: first.id,
              name: first.full_name,
              specialty: first.specialty || 'General Practice',
              avatar: first.profile_image ? `${process.env.REACT_APP_API_URL}${first.profile_image}` : null,
              rating: first.successRate ? (first.successRate / 20).toFixed(1) : '4.5',
              reviews: first.cases ? Math.floor(first.cases * 0.7) : 0,
              rate: first.rate || 2500,
            });
            setSelectedLawyerId(String(first.id));
          }
        }
      } catch (err) {
        console.error('Failed to fetch lawyers:', err);
      } finally {
        setLawyerLoading(false);
      }
    };
    fetchLawyers();
  }, [lawyerIdParam]);

  const handleLawyerChange = (id) => {
    const found = allLawyers.find(l => String(l.id) === String(id));
    if (found) {
      setSelectedLawyerId(String(found.id));
      setLawyerInfo({
        id: found.id,
        name: found.full_name,
        specialty: found.specialty || 'General Practice',
        avatar: found.profile_image ? `${process.env.REACT_APP_API_URL}${found.profile_image}` : null,
        rating: found.successRate ? (found.successRate / 20).toFixed(1) : '4.5',
        reviews: found.cases ? Math.floor(found.cases * 0.7) : 0,
        rate: found.rate || 2500,
      });
    }
  };

  const handleCopyUpi = () => {
    if (paymentSettings.upi_id) {
      navigator.clipboard.writeText(paymentSettings.upi_id);
      setCopiedUpi(true);
      setTimeout(() => setCopiedUpi(false), 2000);
    }
  };

  if (user?.user_type === 'lawyer') {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-10 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="w-8 h-8 text-danger-500" />
          </div>
          <h2 className="text-xl font-bold text-secondary-900 mb-2">Access Restricted</h2>
          <p className="text-secondary-500 mb-6">Lawyers cannot book consultations. This feature is available for clients only.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary gap-2">
            <FiArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const timeSlots = [
    { time: '9:00 AM', available: true }, { time: '10:00 AM', available: true },
    { time: '11:00 AM', available: true }, { time: '2:00 PM', available: true },
    { time: '3:00 PM', available: true }, { time: '4:00 PM', available: false },
    { time: '5:00 PM', available: true }, { time: '6:00 PM', available: true }
  ];

  const servicePrice = serviceType === 'video' ? 2500 : serviceType === 'phone' ? 2000 : 3500;
  const durationHours = parseInt(duration) / 60;
  const serviceFee = (servicePrice * durationHours).toFixed(2);
  const platformFee = 250;
  const tax = ((parseFloat(serviceFee) + platformFee) * 0.18).toFixed(2);
  const total = (parseFloat(serviceFee) + platformFee + parseFloat(tax)).toFixed(2);

  const handleNext = (e) => {
    e?.preventDefault();
    if (currentStep === 2 && !selectedDate) { alert('Please select a date and time'); return; }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };
  const handlePrev = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };
  const handleBooking = (e) => {
    e.preventDefault();
    if (!selectedDate || !agreeTerms) { alert('Please fill in all required fields'); return; }
    if ((paymentMethod === 'upi' || paymentMethod === 'bank') && !transactionId.trim()) {
      alert('Please enter your Transaction/UTR number to confirm payment');
      return;
    }
    alert(`Booking confirmed for ${selectedDate} at ${selectedTime}. Total: ₹${total}\nPayment Method: ${paymentMethod === 'upi' ? 'BHIM UPI' : paymentMethod === 'qr' ? 'QR Code' : 'Bank Transfer'}\nTransaction ID: ${transactionId}`);
    navigate('/dashboard');
  };

  const steps = [
    { num: 1, label: 'Service', icon: <FiVideo className="w-4 h-4" /> },
    { num: 2, label: 'Schedule', icon: <FiCalendar className="w-4 h-4" /> },
    { num: 3, label: 'Review', icon: <FiCheckCircle className="w-4 h-4" /> },
    { num: 4, label: 'Payment', icon: <FiCreditCard className="w-4 h-4" /> },
  ];

  const services = [
    { id: 'video', icon: <FiVideo className="w-6 h-6" />, name: 'Video Call', price: 2500, desc: 'Face-to-face video consultation', color: 'from-primary-500 to-primary-600' },
    { id: 'phone', icon: <FiPhone className="w-6 h-6" />, name: 'Phone Call', price: 2000, desc: 'Phone-based consultation', color: 'from-teal-500 to-teal-600' },
    { id: 'inperson', icon: <FiMapPin className="w-6 h-6" />, name: 'In-Person', price: 3500, desc: 'Office consultation', color: 'from-accent-500 to-accent-600' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-14 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-bold text-white mb-3">Book Your Consultation</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg">Schedule a professional legal consultation</motion.p>
        </div>
      </div>

      {/* Steps Indicator */}
      <div className="max-w-3xl mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-4 flex items-center justify-between">
          {steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => { if (step.num < currentStep) setCurrentStep(step.num); }}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all ${
                  currentStep > step.num ? 'bg-teal-500 text-white' : currentStep === step.num ? 'bg-primary-500 text-white' : 'bg-secondary-100 text-secondary-400'
                }`}>
                  {currentStep > step.num ? <FiCheckCircle className="w-5 h-5" /> : step.num}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${currentStep >= step.num ? 'text-secondary-900' : 'text-secondary-400'}`}>{step.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${currentStep > step.num ? 'bg-teal-500' : 'bg-secondary-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2">
            <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 md:p-8">

              {/* Step 1: Service */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Select Service Type</h2><p className="text-secondary-500 text-sm">Choose the consultation type that fits your needs</p></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {services.map(s => (
                      <div key={s.id} onClick={() => setServiceType(s.id)} className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${serviceType === s.id ? 'border-primary-500 bg-primary-50/50 shadow-md' : 'border-secondary-100 hover:border-secondary-200'}`}>
                        {serviceType === s.id && <div className="absolute top-3 right-3"><FiCheckCircle className="w-5 h-5 text-primary-500" /></div>}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white mb-3`}>{s.icon}</div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{s.name}</h3>
                        <p className="text-xs text-secondary-500 mb-2">{s.desc}</p>
                        <p className="font-bold text-primary-500">₹{s.price}<span className="text-xs text-secondary-400 font-normal">/hr</span></p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="label">Session Duration</label>
                    <div className="flex gap-3">
                      {['30', '60', '90', '120'].map(dur => (
                        <button key={dur} type="button" onClick={() => setDuration(dur)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${duration === dur ? 'bg-primary-500 text-white border-primary-500' : 'border-secondary-200 text-secondary-600 hover:border-primary-300'}`}>
                          {dur === '120' ? '2 hrs' : `${dur} min`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><label className="label">Describe Your Issue</label><textarea className="textarea" rows="4" placeholder="Briefly describe your legal concern..." value={description} onChange={(e) => setDescription(e.target.value)} /></div>
                </div>
              )}

              {/* Step 2: Schedule */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Select Date & Time</h2><p className="text-secondary-500 text-sm">Choose your preferred consultation slot</p></div>
                  <div><label className="label">Select Date</label><input type="date" className="input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} min={new Date().toISOString().split('T')[0]} /></div>
                  <div>
                    <label className="label">Available Time Slots</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {timeSlots.map(slot => (
                        <button key={slot.time} type="button" disabled={!slot.available} onClick={() => slot.available && setSelectedTime(slot.time)}
                          className={`p-3 rounded-xl border text-center transition-all ${!slot.available ? 'bg-secondary-50 border-secondary-100 text-secondary-300 cursor-not-allowed' : selectedTime === slot.time ? 'bg-primary-500 border-primary-500 text-white shadow-md' : 'border-secondary-200 text-secondary-600 hover:border-primary-300'}`}>
                          <span className="block text-sm font-medium">{slot.time}</span>
                          <span className={`text-xs ${!slot.available ? 'text-secondary-300' : selectedTime === slot.time ? 'text-white/70' : 'text-secondary-400'}`}>
                            {slot.available ? 'Available' : 'Booked'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><label className="label">Additional Notes</label><textarea className="textarea" rows="3" placeholder="Any specific questions or concerns?" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                </div>
              )}

              {/* Step 3: Review */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Review Your Booking</h2><p className="text-secondary-500 text-sm">Please review before proceeding to payment</p></div>
                  <div className="space-y-4">
                    <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100">
                      <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2"><FiVideo className="w-4 h-4 text-primary-400" /> Service Details</h3>
                      {[
                        ['Service Type', serviceType === 'video' ? 'Video Call' : serviceType === 'phone' ? 'Phone Call' : 'In-Person'],
                        ['Duration', `${duration} minutes`],
                        ['Date', selectedDate],
                        ['Time', selectedTime],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between py-1.5"><span className="text-sm text-secondary-500">{label}</span><span className="text-sm font-medium text-secondary-900">{value}</span></div>
                      ))}
                    </div>
                    {description && <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100"><h3 className="font-semibold text-secondary-900 mb-2 text-sm">Your Issue</h3><p className="text-sm text-secondary-600">{description}</p></div>}
                    {notes && <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-100"><h3 className="font-semibold text-secondary-900 mb-2 text-sm">Additional Notes</h3><p className="text-sm text-secondary-600">{notes}</p></div>}
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-secondary-200 hover:bg-secondary-50 transition-colors">
                    <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-500 mt-0.5" />
                    <span className="text-sm text-secondary-600">I agree to the booking terms and cancellation policy</span>
                  </label>
                </div>
              )}

              {/* Step 4: Payment */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div><h2 className="text-xl font-bold text-secondary-900 mb-1">Payment Method</h2><p className="text-secondary-500 text-sm">Select your preferred payment method</p></div>
                  <div className="space-y-3">
                    {[
                      { id: 'upi', icon: <FiSmartphone className="w-5 h-5" />, name: 'BHIM UPI', desc: 'Pay via any UPI app' },
                      { id: 'qr', icon: <FiImage className="w-5 h-5" />, name: 'Scan QR Code', desc: 'Scan & pay instantly' },
                      { id: 'bank', icon: <span className="text-lg">🏦</span>, name: 'Bank Transfer', desc: 'Direct bank transfer (NEFT/IMPS)' },
                    ].map(method => (
                      <div key={method.id} onClick={() => setPaymentMethod(method.id)} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === method.id ? 'border-primary-500 bg-primary-50/50' : 'border-secondary-100 hover:border-secondary-200'}`}>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === method.id ? 'border-primary-500' : 'border-secondary-300'}`}>
                          {paymentMethod === method.id && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                        </div>
                        <div className={`${paymentMethod === method.id ? 'text-primary-500' : 'text-secondary-400'}`}>{method.icon}</div>
                        <div>
                          <span className="font-medium text-secondary-700 block">{method.name}</span>
                          <span className="text-xs text-secondary-400">{method.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* UPI Payment Details */}
                  {paymentMethod === 'upi' && (
                    <div className="space-y-4 pt-2">
                      {paymentSettings.upi_id ? (
                        <div className="p-5 rounded-xl bg-gradient-to-br from-primary-50 to-teal-50 border border-primary-100">
                          <p className="text-sm font-semibold text-secondary-700 mb-3">Pay to this UPI ID:</p>
                          <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-secondary-200">
                            <FiSmartphone className="w-5 h-5 text-primary-500 flex-shrink-0" />
                            <span className="font-mono text-base font-semibold text-secondary-900 flex-1">{paymentSettings.upi_id}</span>
                            <button onClick={handleCopyUpi} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-400 transition-colors">
                              {copiedUpi ? <><FiCheck className="w-3.5 h-3.5" /> Copied!</> : <><FiCopy className="w-3.5 h-3.5" /> Copy</>}
                            </button>
                          </div>
                          {paymentSettings.upi_display_name && (
                            <p className="text-xs text-secondary-500 mt-2">Payee: {paymentSettings.upi_display_name}</p>
                          )}
                          <div className="mt-3 p-3 bg-accent-50 rounded-lg border border-accent-200">
                            <p className="text-xs text-accent-700 font-medium">💡 Open any UPI app (Google Pay, PhonePe, Paytm, BHIM) → Send Money → Enter the UPI ID above → Pay ₹{total}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-200 text-center">
                          <FiAlertCircle className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
                          <p className="text-sm text-secondary-500">UPI payment is not configured yet. Please choose another method.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* QR Code Payment */}
                  {paymentMethod === 'qr' && (
                    <div className="space-y-4 pt-2">
                      {paymentSettings.qr_code_image ? (
                        <div className="p-5 rounded-xl bg-gradient-to-br from-primary-50 to-teal-50 border border-primary-100 text-center">
                          <p className="text-sm font-semibold text-secondary-700 mb-4">Scan this QR code to pay ₹{total}</p>
                          <div className="inline-block p-4 bg-white rounded-2xl shadow-card border border-secondary-200">
                            <img src={`${process.env.REACT_APP_API_URL}${paymentSettings.qr_code_image}`} alt="Payment QR Code" className="w-56 h-56 object-contain" />
                          </div>
                          {paymentSettings.upi_display_name && (
                            <p className="text-sm text-secondary-600 mt-3 font-medium">{paymentSettings.upi_display_name}</p>
                          )}
                          <div className="mt-3 p-3 bg-accent-50 rounded-lg border border-accent-200 text-left">
                            <p className="text-xs text-accent-700 font-medium">💡 Open any UPI app → Scan QR Code → Verify the payee name → Pay ₹{total}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-200 text-center">
                          <FiAlertCircle className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
                          <p className="text-sm text-secondary-500">QR code payment is not configured yet. Please choose another method.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bank Transfer Details */}
                  {paymentMethod === 'bank' && (
                    <div className="space-y-4 pt-2">
                      {paymentSettings.bank_account_number ? (
                        <div className="p-5 rounded-xl bg-gradient-to-br from-primary-50 to-teal-50 border border-primary-100">
                          <p className="text-sm font-semibold text-secondary-700 mb-4">Bank Transfer Details:</p>
                          <div className="bg-white rounded-xl border border-secondary-200 divide-y divide-secondary-100">
                            {paymentSettings.bank_name && (
                              <div className="flex justify-between items-center px-4 py-3">
                                <span className="text-sm text-secondary-500">Bank Name</span>
                                <span className="text-sm font-semibold text-secondary-900">{paymentSettings.bank_name}</span>
                              </div>
                            )}
                            {paymentSettings.bank_account_name && (
                              <div className="flex justify-between items-center px-4 py-3">
                                <span className="text-sm text-secondary-500">Account Name</span>
                                <span className="text-sm font-semibold text-secondary-900">{paymentSettings.bank_account_name}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center px-4 py-3">
                              <span className="text-sm text-secondary-500">Account Number</span>
                              <span className="text-sm font-mono font-semibold text-secondary-900">{paymentSettings.bank_account_number}</span>
                            </div>
                            {paymentSettings.bank_ifsc_code && (
                              <div className="flex justify-between items-center px-4 py-3">
                                <span className="text-sm text-secondary-500">IFSC Code</span>
                                <span className="text-sm font-mono font-semibold text-secondary-900">{paymentSettings.bank_ifsc_code}</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-3 p-3 bg-accent-50 rounded-lg border border-accent-200">
                            <p className="text-xs text-accent-700 font-medium">💡 Transfer ₹{total} via NEFT/IMPS/RTGS and enter the UTR number below</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-5 rounded-xl bg-secondary-50 border border-secondary-200 text-center">
                          <FiAlertCircle className="w-8 h-8 text-secondary-300 mx-auto mb-2" />
                          <p className="text-sm text-secondary-500">Bank transfer details not configured yet. Please choose another method.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transaction ID Input - shown for all payment methods */}
                  <div className="pt-2">
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">Transaction / UTR Number <span className="text-danger-500">*</span></label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary-500 transition-all font-mono"
                      placeholder="Enter your transaction/UTR number after payment"
                    />
                    <p className="text-xs text-secondary-400 mt-1.5">Enter the transaction ID you received after completing the payment</p>
                  </div>

                  {paymentSettings.payment_instructions && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-primary-50 text-primary-700 text-sm border border-primary-100">
                      <FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>{paymentSettings.payment_instructions}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 p-3 rounded-xl bg-teal-50 text-teal-600 text-sm"><FiLock className="w-4 h-4" /> Your payment information is secure and encrypted</div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8 pt-6 border-t border-secondary-100">
                {currentStep > 1 && <button onClick={handlePrev} className="btn-ghost py-3 px-6 border border-secondary-200 gap-2"><FiArrowLeft className="w-4 h-4" /> Back</button>}
                <div className="flex-1" />
                {currentStep < 4 && <button onClick={handleNext} className="btn-primary py-3 px-8 gap-2">Next Step <FiArrowRight className="w-4 h-4" /></button>}
                {currentStep === 4 && <button onClick={handleBooking} className="btn-primary py-3 px-8 gap-2"><FiCheckCircle className="w-4 h-4" /> Complete Booking</button>}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Attorney */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Attorney</h3>
              {lawyerLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-secondary-400">Loading...</span>
                </div>
              ) : lawyerInfo ? (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    {lawyerInfo.avatar ? (
                      <img src={lawyerInfo.avatar} alt={lawyerInfo.name} className="w-14 h-14 rounded-xl object-cover ring-2 ring-primary-200" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-xl font-bold shadow-md">
                        {lawyerInfo.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-secondary-900">{lawyerInfo.name}</h4>
                      <p className="text-sm text-secondary-500">{lawyerInfo.specialty}</p>
                      <div className="flex items-center gap-1 mt-1"><FiStar className="w-3.5 h-3.5 fill-accent-500 text-accent-500" /><span className="text-sm text-secondary-600">{lawyerInfo.rating} ({lawyerInfo.reviews})</span></div>
                    </div>
                  </div>
                  {allLawyers.length > 1 && (
                    <div>
                      <label className="text-xs font-medium text-secondary-500 mb-1 block">Change Attorney</label>
                      <select
                        value={selectedLawyerId}
                        onChange={(e) => handleLawyerChange(e.target.value)}
                        className="w-full text-sm border border-secondary-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-secondary-50"
                      >
                        {allLawyers.map(l => (
                          <option key={l.id} value={l.id}>{l.full_name} — {l.specialty || 'General'}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-secondary-400">No attorneys available</p>
              )}
            </div>

            {/* Price Summary */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Price Summary</h3>
              <div className="space-y-3">
                {[['Service Fee', `₹${serviceFee}`], ['Platform Fee', `₹${platformFee.toFixed(2)}`], ['GST (18%)', `₹${tax}`]].map(([label, value]) => (
                  <div key={label} className="flex justify-between text-sm"><span className="text-secondary-500">{label}</span><span className="text-secondary-700">{value}</span></div>
                ))}
                <div className="border-t border-secondary-100 pt-3 flex justify-between"><span className="font-semibold text-secondary-900">Total</span><span className="font-bold text-xl text-primary-500">₹{total}</span></div>
              </div>
            </div>

            {/* Cancellation */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2"><FiShield className="w-4 h-4 text-primary-400" /> Cancellation Policy</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-secondary-500">Up to 24 hours</span><span className="text-teal-600 font-medium">Free</span></div>
                <div className="flex justify-between text-sm"><span className="text-secondary-500">Less than 24 hours</span><span className="text-accent-600 font-medium">50% charged</span></div>
              </div>
              <p className="text-xs text-secondary-400 mt-3">No-show will incur full charges</p>
            </div>

            {/* Security */}
            <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl border border-primary-200/50 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3"><FiLock className="w-5 h-5 text-white" /></div>
              <h4 className="font-semibold text-secondary-900 mb-1">Secure Payment</h4>
              <p className="text-xs text-secondary-500">All transactions are encrypted and secure</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingModern;
